import {useTheme} from 'styled-components';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

const MARKER_PILL_MIN_WIDTH = 66;
const MARKER_PILL_HEIGHT = 24;
/** Approximate glyph width of the pill font — sizes the pill to its label. */
const MARKER_PILL_CHAR_WIDTH = 7;
const MARKER_PILL_PADDING_X = 16;
/** Text baseline offset from the pill's vertical centre. */
const MARKER_PILL_TEXT_BASELINE = 5;
const MARKER_BEAK_HALF = 5;
const MARKER_BEAK_DEPTH = 6;
/** Marker closer to the pill edge than this switches the beak to the corner shape. */
const MARKER_BEAK_INSET = 10;
/** How far the corner beak reaches up the pill edge to cover its rounded corner. */
const MARKER_BEAK_CORNER_COVER = 4;
const MARKER_PILL_EDGE_MARGIN = 2;

interface DistributionMarkerProps extends TestIdProps {
  /** X position of the price line; null hides the marker entirely. */
  markerX: number | null;
  /** Total chart width — clamps the pill inside the chart. */
  width: number;
  /** Top of the pill (just inside the tinted zone so it never covers the % labels). */
  pillY: number;
  baselineY: number;
  color: string;
  /** Colour of the pill text and the dot outline (the chart paper colour). */
  paperColor: string;
  label: string;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/**
 * The price marker: vertical line, baseline dot and a labelled pill. A centred
 * marker gets the usual triangle beak under the pill; at the chart edges the pill
 * stops centring over the line and the beak becomes a speech-bubble corner tail
 * fused with the pill's left/right edge (the ActualPriceChart look).
 */
export function DistributionMarker(props: DistributionMarkerProps) {
  const theme = useTheme();

  if (isNil(props.markerX)) {
    return null;
  }
  const markerX = props.markerX;

  // The pill grows with its label so longer price strings never overflow it.
  const pillWidth = Math.max(
    MARKER_PILL_MIN_WIDTH,
    Math.round(props.label.length * MARKER_PILL_CHAR_WIDTH) + MARKER_PILL_PADDING_X
  );
  const pillX = clamp(
    markerX - pillWidth / 2,
    MARKER_PILL_EDGE_MARGIN,
    props.width - pillWidth - MARKER_PILL_EDGE_MARGIN
  );
  const pillBottom = props.pillY + MARKER_PILL_HEIGHT;
  const pillRight = pillX + pillWidth;
  const beakPath = match(markerX)
    .when(
      (x) => x <= pillX + MARKER_BEAK_INSET,
      () =>
        `M ${pillX} ${pillBottom - MARKER_BEAK_CORNER_COVER} L ${pillX + 2 * MARKER_BEAK_HALF} ${pillBottom} L ${pillX} ${pillBottom + MARKER_BEAK_DEPTH} Z`
    )
    .when(
      (x) => x >= pillRight - MARKER_BEAK_INSET,
      () =>
        `M ${pillRight} ${pillBottom - MARKER_BEAK_CORNER_COVER} L ${pillRight - 2 * MARKER_BEAK_HALF} ${pillBottom} L ${pillRight} ${pillBottom + MARKER_BEAK_DEPTH} Z`
    )
    .otherwise(
      (x) =>
        `M ${x - MARKER_BEAK_HALF} ${pillBottom} L ${x + MARKER_BEAK_HALF} ${pillBottom} L ${x} ${pillBottom + MARKER_BEAK_DEPTH} Z`
    );

  return (
    <g data-testid={suffixTestId('marker', props)}>
      <line
        x1={markerX}
        y1={props.pillY}
        x2={markerX}
        y2={props.baselineY}
        stroke={props.color}
        strokeWidth={2}
      />
      <circle
        cx={markerX}
        cy={props.baselineY}
        r={5}
        fill={props.color}
        stroke={props.paperColor}
        strokeWidth={2}
      />
      <rect
        x={pillX}
        y={props.pillY}
        width={pillWidth}
        height={MARKER_PILL_HEIGHT}
        rx={5}
        fill={props.color}
      />
      <path d={beakPath} fill={props.color} />
      <text
        x={pillX + pillWidth / 2}
        y={props.pillY + MARKER_PILL_HEIGHT / 2 + MARKER_PILL_TEXT_BASELINE}
        fontSize={theme.fontSizes.text.small}
        fontWeight={theme.fontWeights.text.default}
        fill={props.paperColor}
        textAnchor="middle"
      >
        {props.label}
      </text>
    </g>
  );
}
